import {HTMLAttributes, ReactNode, useMemo, useState} from 'react';
import {SchemeLink} from '../../../../applink/SchemeLink';
import {t} from '../../../../common/getTranslations';
import {requestContext} from '../../../../common/requestContext';
import {twMerge} from '../../../../common/twMerge';
import {Icon} from '../../../../design-system/atoms/Icon/Icon';
import {IconType} from '../../../../design-system/atoms/Icon/IconType.enum';
import {Text} from '../../../../design-system/atoms/Text/Text';
import {ContentHomeCardSection} from '../../../../design-system/organisms/ContentHomeCardSection/ContentHomeCardSectionMain';
import {SummaryType} from '../../../../design-system/organisms/ContentHomeCardSection/ContentHomeSectionBody/SummaryType.enum';
import {
  ContentHomeAbout,
  ContentHomeRecommend,
  Maybe,
} from '../../../../graphql/generated/types';
import {showImagePopup} from '../../../../imagePopup/imagePopupHandler';
import {LOG_CLICK_EVENT} from '../../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {ONLY_DATA_CONFIG} from '../../../../reactQuery/constant';
import {useContentHomeOverviewQuery} from '../../../../wrapper/contentWrapper';
import {ContentHomeRecommendContainer} from './ContentHomeRecommendContainer';

export interface ContentHomeAboutContainerProps
  extends HTMLAttributes<HTMLDivElement> {
  seriesId: number;
  aboutData: Maybe<ContentHomeAbout>;
  recommendData: Maybe<ContentHomeRecommend>;
}

export function ContentHomeAboutContainer({
  seriesId,
  aboutData,
  recommendData,
  ...props
}: ContentHomeAboutContainerProps) {
  const {isMobile} = requestContext.requestInfo.display;
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const [isCharacterExpanded, setIsCharacterExpanded] = useState(false);

  const overview = useContentHomeOverviewQuery({seriesId}, ONLY_DATA_CONFIG)
    .data?.contentHomeOverview;

  const DetailInfoWrapper = useMemo(() => {
    const wrapperComponent = ({children}: {children: ReactNode}) =>
      isMobile ? (
        <>{children}</>
      ) : (
        <div className="flex w-full flex-col space-y-4pxr rounded-b-12pxr bg-theme-bg-surface-alt">
          {children}
        </div>
      );

    return wrapperComponent;
  }, [isMobile]);

  return (
    <div
      className={twMerge(
        'flex flex-col',
        isMobile ? 'space-y-8pxr' : 'space-y-4pxr'
      )}
      {...props}
    >
      <DetailInfoWrapper>
        {aboutData?.detail?.rank && (
          <SchemeLink
            scheme={`kakaopage://open/landing/ranking?category_uid=${overview?.content?.categoryUid}&subcategory_uid=${overview?.content?.subcategoryUid}&ranking_type=weekly`}
          >
            <div
              className={twMerge(
                'flex h-48pxr items-center justify-between',
                'rounded-8pxr px-18pxr',
                'cursor-pointer',
                isMobile && 'bg-theme-solid-10',
                !isMobile && 'bg-theme-solid-20 mx-11pxr'
              )}
              onClick={e => LOG_CLICK_EVENT('랭킹_클릭', e)}
              {...putTiaraObjectProps({
                click: {layer1: '랭킹'},
              })}
            >
              <div className="flex items-center space-x-4pxr">
                <Text className="font-small1 text-theme-solid-100">
                  {t('contenthome_info_tab_ranking', {
                    values: {
                      category: overview?.content?.category,
                      genre: overview?.content?.subcategory,
                      value: aboutData?.detail?.rank,
                    },
                  })}
                </Text>
              </div>
              <Icon type={IconType.ContentHomeArrowSolid} />
            </div>
          </SchemeLink>
        )}
        {aboutData?.description && (
          <ContentHomeCardSection>
            <ContentHomeCardSection.Header>
              <ContentHomeCardSection.Header.Title>
                {t('contenthome_info_tab_summary')}
              </ContentHomeCardSection.Header.Title>
            </ContentHomeCardSection.Header>
            <ContentHomeCardSection.Body>
              {aboutData?.guideTitle && (
                <ContentHomeCardSection.Body.ContentGuide>
                  {aboutData.guideTitle}
                </ContentHomeCardSection.Body.ContentGuide>
              )}
              <ContentHomeCardSection.Body.Summary
                expand={isSummaryExpanded}
                type={SummaryType.About}
                isMobile={isMobile}
                onClickExpand={() => {
                  setIsSummaryExpanded(true);
                }}
              >
                {aboutData?.description}
              </ContentHomeCardSection.Body.Summary>
            </ContentHomeCardSection.Body>
          </ContentHomeCardSection>
        )}
      </DetailInfoWrapper>
      {aboutData?.themeKeywordList && aboutData.themeKeywordList.length > 0 && (
        <ContentHomeCardSection>
          <ContentHomeCardSection.Header>
            <ContentHomeCardSection.Header.Title>
              {t('contenthome_info_tab_keywords')}
            </ContentHomeCardSection.Header.Title>
          </ContentHomeCardSection.Header>
          <ContentHomeCardSection.Body>
            <ContentHomeCardSection.Body.Keywords
              keywords={aboutData.themeKeywordList.map(keyword => ({
                title: keyword.title as string,
                scheme: keyword.scheme as string,
              }))}
            />
          </ContentHomeCardSection.Body>
        </ContentHomeCardSection>
      )}
      {aboutData?.characterList && aboutData.characterList.length > 0 && (
        <ContentHomeCardSection>
          <ContentHomeCardSection.Header>
            <ContentHomeCardSection.Header.Title>
              {t('contenthome_info_tab_character')}
            </ContentHomeCardSection.Header.Title>
          </ContentHomeCardSection.Header>
          <ContentHomeCardSection.Body>
            <ContentHomeCardSection.Body.Characters
              expand={isCharacterExpanded}
              isMobile={isMobile}
              onClickExpand={e =>
                LOG_CLICK_EVENT('등장인물더보기_클릭', e, () =>
                  setIsCharacterExpanded(true)
                )
              }
              {...putTiaraObjectProps({
                click: {layer1: '더보기'},
              })}
              characters={aboutData.characterList.map(character => ({
                thumbnail: character?.thumbnail as string,
                name: character?.name as string,
                description: character?.description as string,
              }))}
            />
          </ContentHomeCardSection.Body>
        </ContentHomeCardSection>
      )}
      {recommendData && (
        <ContentHomeRecommendContainer
          seriesId={seriesId}
          recommendData={recommendData}
        />
      )}
      {aboutData?.screenshotList && aboutData.screenshotList.length > 0 && (
        <ContentHomeCardSection>
          <ContentHomeCardSection.Header>
            <ContentHomeCardSection.Header.Title>
              {t('contenthome_info_tab_screenshoot')}
            </ContentHomeCardSection.Header.Title>
          </ContentHomeCardSection.Header>
          <ContentHomeCardSection.Body>
            <ContentHomeCardSection.Body.Screenshot
              images={aboutData.screenshotList.map(screenshot => screenshot)}
              isMobile={isMobile}
              {...putTiaraObjectProps({click: {layer1: '관련스크린샷'}})}
              onClickImage={(image, index, e) => {
                LOG_CLICK_EVENT('관련스크린샷_클릭', e, () =>
                  showImagePopup({
                    imageList: isMobile
                      ? aboutData?.screenshotList
                      : [aboutData?.screenshotList[index]],
                    isMobile,
                    isPoster: false,
                    focusIn: isMobile ? index : undefined,
                    style: {cursor: 'pointer'},
                  })
                );
              }}
            />
          </ContentHomeCardSection.Body>
        </ContentHomeCardSection>
      )}
      {aboutData?.detailInfoList && (
        <ContentHomeCardSection>
          <ContentHomeCardSection.Header>
            <ContentHomeCardSection.Header.Title>
              {t('contenthome_info_tab_series_detail')}
            </ContentHomeCardSection.Header.Title>
          </ContentHomeCardSection.Header>
          <ContentHomeCardSection.Body>
            <ContentHomeCardSection.Body.DetailInfo
              infoList={aboutData?.detailInfoList}
            />
          </ContentHomeCardSection.Body>
        </ContentHomeCardSection>
      )}
    </div>
  );
}
